<template>
  <AppModalEdit
    :value="value"
    :loading="hasQueue"
    @input="$emit('input', $event)"
    @clickExec="onSubmit"
    title="一括地点登録"
    exec-button-text="一括登録"
    help-page-title="地点一括登録 ヘルプ と CSVフォーマットはこちら"
    help-page-url="https://go.geologic.co.jp/adminmanual_location_location_bulk"
  >
    <p>
      入力フォームに直接入力するか、CSVファイルをドラッグ＆ドロップしてください。
    </p>
    <AppFileDropArea :mime-types="mimeTypes" @files="onDropCSVFile">
      <v-layout class="dropArea pa-2 mb-1 justify-center align-center">
        <v-icon>file_copy</v-icon>
        CSVファイルをドラッグできます
      </v-layout>
      <template v-slot:drag>
        <v-layout
          class="dropArea dragging pa-2 mb-1 justify-center align-center"
        >
          <v-icon>file_copy</v-icon>
          ドロップしてCSVファイルの内容を読み込む
        </v-layout>
      </template>
    </AppFileDropArea>
    <p v-if="hasError" style="padding-top: 20px">
      エラー地点一覧 {{ errorPointsCountText }}
    </p>
    <v-textarea
      ref="bulkInputArea"
      v-if="hasError"
      v-model="form.errorLocationCsv"
      hide-details
      readonly
      outline
      rows="3"
      style="white-space: pre-line; background-color: #C0C0C0; margin-bottom: 10px"
      class="bulkInputArea"
    />
    <div
      v-if="hasError"
      style="display:flex; margin-bottom: 10px; align-items: center"
    >
      <v-btn @click="sendErrorLocationsStringToClipBoard" color="primary">
        エラー地点をコピー
      </v-btn>
      <v-btn
        @click="
          downloadToBeFixedCsv({
            errorLocationCsv: form.errorLocationCsv,
            importableLocationCsv: form.csv,
          })
        "
        color="primary"
      >
        <v-icon style="margin-right: 5px">save_alt</v-icon>
        修正用CSVファイルをダウンロード
      </v-btn>
      <p style="color:red; margin-bottom: 0px;">
        エラー地点の内容を修正後再度CSVファイルを読み込んでいただくか、入力欄へ修正内容を追記し登録ボタンを押してください。
      </p>
    </div>
    <p>
      入稿地点一覧 ※
      <code>地点名,住所</code>のフォーマットで入力してください。
    </p>
    <v-textarea
      v-model="form.csv"
      :placeholder="`地点名1,住所1\n地点名2,住所2`"
      :color="hasError ? 'red' : 'primary'"
      :background-color="hasError ? 'red' : undefined"
      :hide-details="!hasError"
      :error="hasError"
      rows="20"
      outline
      class="bulkInputArea"
      style="white-space: pre-line;"
    />
    <p v-if="hasError" style="color:red;">
      エラー地点一覧に表示されている内容を登録しない場合、そのまま再度登録ボタンを押してください。
    </p>
  </AppModalEdit>
</template>
<script>
import debounce from 'lodash/debounce'
import AppModalEdit from '@/components/App/AppModalEdit'
import AppFileDropArea from '@/components/App/AppFileDropArea'
import { BulkAddLocationFormModel } from '@/models/Location'
import { mimeTypes } from '@/components/App/AppFileDropArea'
import { getGeocoder } from '@/models/Location'
import {
  makeTemporaryId,
  SimulationLocation,
} from '@/models/Simulation/SimulationFormDelivery'
import { mapActions } from 'vuex'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import { createBlobFromLocationCsv } from '@/models/LocationGroup/CreateBlobFromLocationCsv'
import { extractImportableAndErrorLocations } from '@/models/Location/ExtractImportableAndErrorLocationsForLocationBulk'

const makeCSV = locations =>
  locations
    .map(location => Object.values(location).join(`,`))
    .join(`\n`)
    .trim()

export default {
  name: 'SimulationContentDeliveryContentLocationBulkAddModal',
  components: { AppFileDropArea, AppModalEdit },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    locationGroup: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      geocoder: getGeocoder(),
      form: new BulkAddLocationFormModel({
        locationGroup: this.locationGroup,
        geocoder: this.geocoder,
      }),
      queue: [],
      consumerTimer: null,
      loading: false,
      errorLocations: [],
      hasError: false,
      errorPointsCountText: '',
    }
  },
  computed: {
    mimeTypes() {
      return Object.values(mimeTypes.csv)
    },
    hasQueue() {
      return this.queue.length !== 0
    },
  },
  watch: {
    'form.csv'() {
      this.debouncedQueueAddress()
    },
  },
  created() {
    // 入力途中のものをできるだけ省く
    this.debouncedQueueAddress = debounce(this.queueAddress, 500)
    this.consumeAddress()
  },
  beforeDestroy() {
    clearInterval(this.consumerTimer)
  },
  methods: {
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    closeModal() {
      this.$emit('input', false)
    },
    queueAddress() {
      const addresses = this.form.parseAndDistinctLocations.map(
        ({ address }) => address,
      )
      this.queue = [...this.queue, ...addresses]
    },
    consumeAddress() {
      this.consumerTimer = setInterval(() => {
        const address = this.queue.shift()
        address && this.geocoder(address)
      }, 20)
    },
    onDropCSVFile(files) {
      const [file] = files
      this.form.readCSVFile(file)
    },
    async onSubmit() {
      const geocoder = this.geocoder
      const parsedLocations = this.parseAndDistinctLocations()
      const locations = await extractImportableAndErrorLocations({
        parsedLocations,
        geocoder,
      })
      const importableLocations = locations.importableLocations
      const errorLocations = locations.errorLocations
      if (errorLocations.length > 0) {
        this.form.csv = makeCSV(importableLocations)
        this.form.errorLocationCsv = makeCSV(errorLocations)
        this.hasError = true
        this.errorPointsCountText = `${errorLocations.length} 地点`
        alert(
          `地点登録が完了できませんでした。\nエラー地点一覧の内容をご確認ください。`,
        )
        return
      }
      const importLocation = await Promise.all(
        importableLocations.map(async ({ name, address }, i) => {
          const latLng = await this.geocoder(address)
          const { lat, lng } = latLng[0]
          const id = makeTemporaryId(i)
          console.log(latLng, lat, lng, id, name)
          const location = SimulationLocation.makeLocationData({
            id,
            name,
            lng,
            lat,
          })
          return SimulationLocation.assign(location)
        }),
      )
      this.locationGroup.addManyLocation(importLocation)
      this.closeModal()
      this.showSuccessSnackBar()
    },
    parseAndDistinctLocations() {
      return this.form.parseAndDistinctLocations.filter(location => {
        if (
          (location.name === undefined || location.name === '') &&
          (location.address === undefined || location.address === '')
        )
          return false
        return true
      })
    },
    showSuccessSnackBar() {
      this.showSnackbar({
        type: snackbarType.SUCCESS,
        message: '地点を一括登録しました',
      })
    },
    sendErrorLocationsStringToClipBoard() {
      navigator.clipboard.writeText(this.form.errorLocationCsv)
    },
    downloadToBeFixedCsv({ errorLocationCsv, importableLocationCsv }) {
      const blob = createBlobFromLocationCsv({
        errorLocationCsv,
        importableLocationCsv,
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = '一括入稿修正フォーマット.csv'
      link.click()
    },
  },
}
</script>
<style scoped lang="scss">
.bulkInputArea {
  ::v-deep .v-text-field__slot > textarea {
    margin-top: 0;
    font-size: 14px;
  }
}

.dropArea {
  border: 4px dotted #666;
  border-radius: 4px;
  height: 50px;
  transition: all 0.5s linear;

  &.dragging {
    height: 200px;
    background-color: lighten(lightgreen, 5%);
  }
}
</style>
